import React, {FC, useEffect, useState} from 'react'
import styled from 'styled-components'
import SvgCheck from 'nf-ui/Icons/Check'
import {Spinner} from 'nf-ui'
import {orderBy} from 'lodash'
import {CellValue} from './types'

const MenuEntryContainer = styled.div`
    max-height: 38vh;
    min-height: 43px;
    min-width: 200px;
    overflow: auto;
    &:not(:hover) > .highlighted {
        background-color: #eee;
    }
    &:hover > .highlighted:not(.allow-hover-highlight) {
        background-color: #eee;
    }
`

const MenuEntry = styled.div`
    display: flex;
    justify-content: space-between;
    font-family: 'halyard-display';
    font-size: 16px;
    font-weight: 300;
    padding: 0px 0px;
    background-color: white;
    border-top: 1px solid #d5dbdf;
    border-width: 1px 0px;
    border-radius: 0px;
    > * {
        padding: 16px 8px;
    }
    &.selected {
        font-weight: 500;
    }
    > :not(input) {
        cursor: pointer;
    }
    .edit {
        padding: 18px 8px 16px;
    }
    .check {
        margin: -4px 0px -2px;
        padding: 6px 6px 0px;
    }
    &.allow-hover-highlight:hover {
        background-color: #eee;
    }
    &.can-initiate-edit:not(:hover) {
        padding: 0px 32px 0px 0px;
    }
    &:not(:hover),
    &:not(.can-initiate-edit) {
        > .edit {
            display: none;
        }
    }
    &.sticky-top {
        position: sticky;
        top: 0px;
    }
    &.sticky-bottom {
        position: sticky;
        bottom: 0px;
        padding: 3px 0px;
        color: #3c8eff;
        font-weight: 500;
    }
`

const SpinnerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    > * {
        height: 20px;
    }
`

export interface PickReportsToProps {
    availableValues: {value: string; valueIdStr: string}[]
    selectedValue: CellValue | undefined
    searchValue: string
    mutating: {mutation: string; col?: number; row?: number} | undefined
    setMutating: (value: {mutation: string; col?: number; row?: number} | undefined) => void
    pickValue: (value: {value: string; valueIdStr: string} | null, onComplete: () => void) => void
    cellEditorHighlight: number
}

export const PickReportsTo: FC<PickReportsToProps> = (props) => {
    const [allowHoverHighlight, setAllowHoverHighlight] = useState(false)

    const filtered = orderBy(
        props.availableValues.filter((value) =>
            props.searchValue.split(' ').every((part) => value.value.toLowerCase().includes(part.toLowerCase())),
        ),
        (value) => value.value,
    )

    const initialScrollItemRef = React.createRef<HTMLDivElement>()

    useEffect(() => {
        initialScrollItemRef?.current?.scrollIntoView({block: 'center'})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <MenuEntryContainer>
            {filtered.map((value, index) => {
                const isSelected = props.selectedValue?.values?.includes(value.valueIdStr)
                return (
                    <MenuEntry
                        className={`${isSelected ? 'selected' : ''} ${
                            index === props.cellEditorHighlight % filtered.length ? 'highlighted' : ''
                        } ${allowHoverHighlight ? 'allow-hover-highlight' : ''}`}
                        key={index}
                        ref={isSelected ? initialScrollItemRef : undefined}
                        onMouseMove={() => !allowHoverHighlight && setAllowHoverHighlight(true)}
                    >
                        {props.mutating?.mutation !== `pick-value-${value.valueIdStr}` ? (
                            <span
                                onClick={() => {
                                    if (isSelected) return
                                    props.setMutating({mutation: `pick-value-${value.valueIdStr}`})
                                    props.pickValue(isSelected ? null : value, () => {
                                        props.setMutating(undefined)
                                    })
                                }}
                            >
                                <span>{value.value}</span>
                                {isSelected && <SvgCheck className="check"></SvgCheck>}
                                <span></span>
                            </span>
                        ) : (
                            <SpinnerContainer>
                                <Spinner></Spinner>
                            </SpinnerContainer>
                        )}
                    </MenuEntry>
                )
            })}
            {props.selectedValue?.values?.length && !props.searchValue ? (
                <MenuEntry className="sticky-bottom">
                    {props.mutating?.mutation !== 'clear-value' ? (
                        <span
                            onClick={async () => {
                                props.setMutating({mutation: 'clear-value'})
                                props.pickValue(null, () => {
                                    props.setMutating(undefined)
                                })
                            }}
                        >
                            Clear Selection
                        </span>
                    ) : (
                        <SpinnerContainer>
                            <Spinner></Spinner>
                        </SpinnerContainer>
                    )}
                </MenuEntry>
            ) : null}
        </MenuEntryContainer>
    )
}
